import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { api } from '../lib/api';

function getToken() {
  return localStorage.getItem('mickey_token');
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [crmStats, setCrmStats] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [keeSession, setKeeSession] = useState(null);
  const [trafficData, setTrafficData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await api.get('/dashboard'));
      } catch (e) {
        // The shared helper handles expired sessions and exposes HTTP status.
        // A 401 is already on its way to sign-in, so show no words for that instant.
        if (e.status !== 401) setError(e.status ? `Failed to load dashboard (${e.status})` : 'Network error loading dashboard');
      } finally {
        setLoading(false);
      }
    })();

    // Load CRM stats + My Day tasks in parallel
    (async () => {
      try {
        setCrmStats(await api.get('/crm/stats'));
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        const token = getToken();
        let owner = null;
        try {
          owner = JSON.parse(atob(token.split('.')[1])).email || null;
        } catch { /* ignore */ }
        setTaskStats(await api.get(owner ? `/crm/tasks/stats?owner=${encodeURIComponent(owner)}` : '/crm/tasks/stats'));
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        const json = await api.get('/kee/my-session');
        if (json.hasPendingSession) setKeeSession(json);
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        setTrafficData(await api.get('/dashboard/traffic'));
      } catch { /* ignore */ }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500 dark:text-gray-400">
        <div className="w-8 h-8 border-3 border-gray-200 dark:border-slate-600 border-t-primary rounded-full animate-spin" />
        <p className="text-sm">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-8">
        <Alert variant="error">{error}</Alert>
        <Button variant="secondary" className="mt-4" onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Welcome to {data.tenant?.name || 'Your Dashboard'}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Logged in as {data.user?.email}
        </p>
      </div>

      {/* KEE Daily Session Notification */}
      {keeSession && (
        <div
          className="flex items-center gap-4 p-4 mb-6 rounded-lg border cursor-pointer transition-all hover:shadow-md"
          style={{
            background: 'linear-gradient(135deg, rgba(38, 166, 154, 0.08), rgba(77, 182, 172, 0.04))',
            borderColor: 'rgba(38, 166, 154, 0.2)',
          }}
          onClick={() => navigate('/kee')}
        >
          <div
            className="flex items-center justify-center rounded-full flex-shrink-0"
            style={{ width: 48, height: 48, background: 'rgba(38, 166, 154, 0.12)', color: '#26A69A' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a8 8 0 0 0-8 8c0 5 8 12 8 12s8-7 8-12a8 8 0 0 0-8-8z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              You have {keeSession.questions.length} KEE question{keeSession.questions.length !== 1 ? 's' : ''} waiting
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {keeSession.gamification?.current_streak > 0
                ? `${keeSession.gamification.current_streak}-day streak — keep it going!`
                : 'Start your daily knowledge session — only takes 3-5 minutes'}
            </p>
          </div>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white flex-shrink-0"
            style={{ background: '#26A69A' }}
            onClick={(e) => { e.stopPropagation(); navigate('/kee'); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            Start Session
          </button>
        </div>
      )}

      {data.admin && <AdminStats admin={data.admin} />}

      {/* CRM Pipeline + My Day + Traffic */}
      {(crmStats || taskStats || trafficData) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          {crmStats && (
            <Card className="p-4 cursor-pointer hover:border-primary transition-colors" onClick={() => navigate('/leads')}>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Pipeline</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{crmStats.total || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Active leads</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {crmStats.pipelineValue >= 1000 ? `$${(crmStats.pipelineValue / 1000).toFixed(0)}k` : `$${crmStats.pipelineValue || 0}`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pipeline value</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {crmStats.winRate != null ? `${crmStats.winRate}%` : '—'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Win rate</p>
                </div>
              </div>
            </Card>
          )}
          {taskStats && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">My Day</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className={`text-2xl font-bold ${taskStats.overdue > 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                    {taskStats.overdue || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Overdue</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.today || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Today</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.week || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">This week</p>
                </div>
              </div>
            </Card>
          )}
          {trafficData && <TrafficKPI data={trafficData} />}
        </div>
      )}

      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Your Modules</h2>
        <ModuleGrid modules={data.modules} onNavigate={(path) => navigate(path)} />
      </section>
    </div>
  );
}

/* ── Traffic KPI ─────────────────────────────────── */

function fmtCount(n) {
  if (n == null) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
}

function TrafficKPI({ data }) {
  if (!data) return null;

  if (!data.configured) {
    return (
      <Card className="p-4 border border-dashed">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Site Traffic</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">concria.us — analytics not connected</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Set <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">CF_ANALYTICS_TOKEN</code> +{' '}
          <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">CF_ZONE_TAG</code> to connect Cloudflare Web Analytics,
          or <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">GA4_PROPERTY_ID</code> +{' '}
          <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">GA4_SERVICE_ACCOUNT_KEY</code> for GA4.
        </p>
      </Card>
    );
  }

  const d7 = data.last7d || {};
  const d30 = data.last30d || {};
  const providerLabel = data.provider === 'cloudflare' ? 'Cloudflare Analytics' : 'Google Analytics 4';

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Site Traffic</h3>
        <span className="text-[10px] text-gray-400">{providerLabel}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmtCount(d7.pageviews)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Pageviews · 7d</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmtCount(d7.visitors)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Visitors · 7d</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">{fmtCount(d30.pageviews)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Pageviews · 30d</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">{fmtCount(d30.visitors)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Visitors · 30d</p>
        </div>
      </div>
    </Card>
  );
}

/* ── Admin Stats ─────────────────────────────────── */

function AdminStats({ admin }) {
  if (!admin) return null;

  const { userCount, apiSpend, recentActivity, budget: tenantBudget } = admin;
  const budget = tenantBudget || 500;

  let spendStatus = 'good';
  if (apiSpend.totalCost >= budget) spendStatus = 'danger';
  else if (apiSpend.totalCost >= budget * 0.7) spendStatus = 'warning';

  return (
    <Card className="p-5">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">System Health</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <StatsWidget label="Active Users" value={userCount} status="good" />
        <StatsWidget label="API Calls (This Month)" value={apiSpend.callCount} status="good" />
        <StatsWidget label="Daily Avg Spend" value={apiSpend.dailyAvg} unit="$" status={spendStatus} />
      </div>

      <SpendGauge spent={apiSpend.totalCost} budget={budget} projected={apiSpend.projected} />

      {recentActivity?.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Recent Admin Activity</h3>
          <ul className="divide-y divide-gray-100 dark:divide-slate-700">
            {recentActivity.map((item, i) => (
              <li key={i} className="flex justify-between py-2 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">{item.action}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {new Date(item.created_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/* ── Stats Widget ────────────────────────────────── */

const statusBorder = { good: 'border-l-success', warning: 'border-l-warning', danger: 'border-l-error' };

function StatsWidget({ label, value, unit, status }) {
  return (
    <Card className={`p-4 border-l-[3px] ${statusBorder[status] || ''}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        {typeof value === 'number' ? formatNumber(value) : value}
        {unit && <span className="text-sm font-medium text-gray-500 dark:text-gray-400 ml-0.5">{unit}</span>}
      </p>
    </Card>
  );
}

function formatNumber(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toFixed(2);
}

/* ── Spend Gauge ─────────────────────────────────── */

function SpendGauge({ spent, budget, projected }) {
  const pct = budget > 0 ? (spent / budget) * 100 : 0;
  const projectedPct = budget > 0 ? (projected / budget) * 100 : 0;

  let barColor = 'bg-primary';
  if (pct >= 100) barColor = 'bg-error';
  else if (pct >= 70) barColor = 'bg-warning';

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">API Spend</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">${spent.toFixed(2)} / ${budget}</span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full relative overflow-hidden">
        <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
        {projectedPct > 0 && projectedPct <= 150 && (
          <div className="absolute top-[-4px] bottom-[-4px] w-0.5 bg-accent" style={{ left: `${Math.min(projectedPct, 100)}%` }} />
        )}
      </div>
      <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-1">
        <span>Projected: ${projected.toFixed(2)}</span>
        <span>{Math.round(pct)}% used</span>
      </div>
    </div>
  );
}

/* ── Module Grid ─────────────────────────────────── */

const MODULE_META = {
  'contract-management': { label: 'Contract Management', description: 'Upload, analyze, and track contracts', path: '/contracts' },
  'crm': { label: 'CRM', description: 'Accounts, contacts, and client relationships', path: '/crm' },
  'tasks': { label: 'Tasks', description: 'Task tracking and project management', path: '/tasks' },
  'kee': { label: 'KEE', description: 'Knowledge Extraction Engine — capture institutional knowledge', path: '/kee' },
  'finance': { label: 'Finance', description: 'Invoicing, billing, and financial tracking', path: '/finance' },
  'email': { label: 'Email', description: 'Email management and automation', path: '/email' },
  'calendar': { label: 'Calendar', description: 'Schedule and event management', path: '/calendar' },
  'chat': { label: 'Chat', description: 'AI-powered assistant', path: '/chat' },
};

function ModuleGrid({ modules, onNavigate }) {
  if (!modules?.length) {
    return <p className="text-center py-8 text-gray-500 dark:text-gray-400">No modules available. Contact your admin for access.</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {modules.map(mod => {
        const meta = MODULE_META[mod.name] || {
          label: mod.name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          description: '',
          path: `/${mod.name}`,
        };

        return (
          <button
            key={mod.name}
            onClick={() => onNavigate?.(meta.path)}
            className="text-left p-5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg hover:border-primary hover:shadow-md transition-all group"
          >
            <div className="w-full h-1 bg-primary/60 group-hover:bg-primary rounded mb-3 transition-colors" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{meta.label}</h3>
            {meta.description && <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{meta.description}</p>}
            <div className="flex gap-1.5">
              {mod.canWrite && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary">Read & Write</span>}
              {!mod.canWrite && mod.canRead && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400">Read Only</span>}
              {mod.canAdmin && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent/10 text-accent">Admin</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
